import type { FieldConfig } from "../types";

/**
 * Base KYB field configuration — all steps, default visibility/required rules.
 * Country and entity-type overrides layer on top of this via FieldConfigOverride.
 */
export const KYB_BASE_FIELDS: FieldConfig[] = [
	// ─── Step 1 — Company Basics ────────────────────────────────────────────────
	{
		fieldId: "businessLegalName",
		step: 1,
		section: "company_basics",
		order: 10,
		label: { en: "Business Legal Name" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Business legal name is required" },
			{
				type: "min_length",
				value: 2,
				message: "Must be at least 2 characters",
			},
		],
		placeholder: { en: "Acme Corp LLC" },
	},
	{
		fieldId: "dba",
		step: 1,
		section: "company_basics",
		order: 20,
		label: { en: "DBA (Doing Business As)" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [{ type: "required", message: "DBA is required" }],
		placeholder: { en: "Acme" },
	},
	{
		fieldId: "previousBusinessNames",
		step: 1,
		section: "company_basics",
		order: 30,
		label: { en: "Previous Business Names" },
		type: "text",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: { en: "Former name, if any" },
	},
	{
		fieldId: "entityType",
		step: 1,
		section: "company_basics",
		order: 40,
		label: { en: "Entity Type" },
		type: "select",
		visible: true,
		required: true,
		validationRules: [{ type: "required", message: "Entity type is required" }],
		options: "ENTITY_TYPES_GENERIC",
	},
	{
		fieldId: "countryOfIncorporation",
		step: 1,
		section: "company_basics",
		order: 50,
		label: { en: "Country of Incorporation" },
		type: "select",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Country of incorporation is required" },
		],
		options: "COUNTRIES_ISO",
	},
	{
		fieldId: "registrationNumber",
		step: 1,
		section: "company_basics",
		order: 60,
		label: { en: "Registration Number" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Registration number is required" },
		],
		placeholder: { en: "e.g. 12345678" },
	},
	{
		fieldId: "dateOfIncorporation",
		step: 1,
		section: "company_basics",
		order: 70,
		label: { en: "Date of Incorporation" },
		type: "date",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Date of incorporation is required" },
		],
	},
	{
		fieldId: "registeredAddress",
		step: 1,
		section: "registered_address",
		order: 80,
		label: { en: "Registered Address" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Registered address is required" },
		],
	},
	{
		fieldId: "website",
		step: 1,
		section: "company_basics",
		order: 90,
		label: { en: "Website" },
		type: "url",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Website is required" },
			{ type: "url", message: "Must be a valid URL" },
		],
		placeholder: { en: "https://example.com" },
	},
	{
		fieldId: "businessDescription",
		step: 1,
		section: "company_basics",
		order: 100,
		label: { en: "Business Description" },
		type: "textarea",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Business description is required" },
			{ type: "max_length", value: 2000, message: "Maximum 2000 characters" },
		],
		placeholder: { en: "Describe your business (max 2000 characters)" },
	},
	{
		fieldId: "expectedMonthlyVolume",
		step: 1,
		section: "company_basics",
		order: 110,
		label: { en: "Expected Monthly Volume (USD)" },
		type: "number",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Expected monthly volume is required" },
			{ type: "min", value: 0, message: "Must be a positive number" },
		],
		placeholder: { en: "e.g. 50000" },
	},
	{
		fieldId: "proofOfBusinessUrl",
		step: 1,
		section: "company_basics",
		order: 120,
		label: { en: "Proof of Business" },
		type: "file",
		visible: true,
		required: false,
		validationRules: [],
		helpText: {
			en: "Upload a document that verifies your business registration.",
		},
	},

	// ─── Step 2 — Wallet & Technical ────────────────────────────────────────────
	{
		fieldId: "walletType",
		step: 2,
		section: "wallet_details",
		order: 10,
		label: { en: "Wallet Type" },
		type: "select",
		visible: true,
		required: true,
		validationRules: [{ type: "required", message: "Wallet type is required" }],
		options: "WALLET_TYPES",
	},
	{
		fieldId: "hostedStatus",
		step: 2,
		section: "wallet_details",
		order: 20,
		label: { en: "Hosted Status" },
		type: "select",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Hosted status is required" },
		],
		options: "HOSTED_STATUS",
	},
	{
		fieldId: "currentlyIntegratedWithStellar",
		step: 2,
		section: "wallet_details",
		order: 30,
		label: { en: "Currently Integrated with Stellar" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "allowP2PInternal",
		step: 2,
		section: "wallet_details",
		order: 40,
		label: { en: "Allow P2P Internal Transfers" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "allowP2PExternal",
		step: 2,
		section: "wallet_details",
		order: 50,
		label: { en: "Allow P2P External Transfers" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "virtualAssetsDetails",
		step: 2,
		section: "wallet_details",
		order: 60,
		label: { en: "Virtual Assets Details" },
		type: "textarea",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Virtual assets details are required" },
		],
		placeholder: { en: "Describe the virtual assets your wallet supports" },
	},
	{
		fieldId: "publicKeySharing",
		step: 2,
		section: "wallet_details",
		order: 70,
		label: { en: "Public Key Sharing" },
		type: "select",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Public key sharing setting is required" },
		],
		options: "PUBLIC_KEY_SHARING",
	},
	{
		fieldId: "isWalletOwnerKYCRequired",
		step: 2,
		section: "wallet_details",
		order: 80,
		label: { en: "Is Wallet Owner KYC Required" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "walletHolderInfoCollected",
		step: 2,
		section: "wallet_holder",
		order: 90,
		label: { en: "Wallet Holder Information Collected" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: {
			en: "Describe what information is collected from wallet holders",
		},
		dependsOn: {
			fieldId: "isWalletOwnerKYCRequired",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "walletHolderInfoUpdateCircumstances",
		step: 2,
		section: "wallet_holder",
		order: 100,
		label: { en: "Circumstances for Wallet Holder Info Update" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "isWalletHolderInfoUpdatedRegularly",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "walletHolderInfoUpdateFrequency",
		step: 2,
		section: "wallet_holder",
		order: 110,
		label: { en: "Wallet Holder Info Update Frequency" },
		type: "text",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: { en: "e.g. Annually" },
		dependsOn: {
			fieldId: "isWalletHolderInfoUpdatedRegularly",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "isWalletHolderInfoUpdatedRegularly",
		step: 2,
		section: "wallet_holder",
		order: 120,
		label: { en: "Is Wallet Holder Info Updated Regularly" },
		type: "boolean",
		visible: true,
		required: false,
		validationRules: [],
	},
	{
		fieldId: "omnibusAccountDescription",
		step: 2,
		section: "wallet_holder",
		order: 130,
		label: { en: "Omnibus Account Description" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: { en: "Describe omnibus account usage, if applicable" },
		dependsOn: {
			fieldId: "walletType",
			operator: "in",
			value: ["Custodial", "Hybrid"],
		},
	},
	{
		fieldId: "otherFundingMethods",
		step: 2,
		section: "wallet_holder",
		order: 140,
		label: { en: "Other Funding Methods" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
	},

	// ─── Step 3 — Ownership Structure ───────────────────────────────────────────
	{
		fieldId: "ownershipTiers",
		step: 3,
		section: "ownership",
		order: 10,
		label: { en: "Ownership Tiers" },
		type: "array",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "At least one ownership tier is required" },
		],
		thresholds: {
			minPercentage: 10,
			uboTrigger: 25,
		},
	},
	{
		fieldId: "hasBoardOfDirectors",
		step: 3,
		section: "board",
		order: 20,
		label: { en: "Has Board of Directors" },
		type: "boolean",
		visible: true,
		required: false,
		validationRules: [],
	},
	{
		fieldId: "boardOfDirectors",
		step: 3,
		section: "board",
		order: 30,
		label: { en: "Board of Directors" },
		type: "array",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "hasBoardOfDirectors",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "consentForBackgroundChecks",
		step: 3,
		section: "consent",
		order: 40,
		label: { en: "Consent for Background Checks" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [
			{
				type: "required",
				message: "Consent for background checks is required",
			},
		],
	},
	{
		fieldId: "anyOwnerConvicted",
		step: 3,
		section: "consent",
		order: 50,
		label: { en: "Any Owner Convicted of Financial Crime" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "convictionDetails",
		step: 3,
		section: "consent",
		order: 60,
		label: { en: "Conviction Details" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [
			{
				type: "required",
				message:
					"Conviction details are required when any owner has been convicted",
				appliesWhen: {
					fieldId: "anyOwnerConvicted",
					operator: "equals",
					value: true,
				},
			},
		],
		dependsOn: {
			fieldId: "anyOwnerConvicted",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "ownershipStructureChartUrl",
		step: 3,
		section: "ownership",
		order: 70,
		label: { en: "Ownership Structure Chart" },
		type: "file",
		visible: true,
		required: false,
		validationRules: [],
		helpText: { en: "Upload a chart showing your ownership structure." },
	},

	// ─── Step 4 — Compliance & Risk ─────────────────────────────────────────────
	{
		fieldId: "isRegulatedEntity",
		step: 4,
		section: "regulatory",
		order: 10,
		label: { en: "We are a regulated entity with licenses/registrations" },
		type: "boolean",
		visible: true,
		required: false,
		validationRules: [],
	},
	{
		fieldId: "licenses",
		step: 4,
		section: "regulatory",
		order: 20,
		label: { en: "Licenses & Registrations" },
		type: "array",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "isRegulatedEntity",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "complianceOfficer",
		step: 4,
		section: "compliance_officer",
		order: 30,
		label: { en: "Compliance Officer" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{
				type: "required",
				message: "Compliance officer information is required",
			},
		],
	},
	{
		fieldId: "sanctionsScreening",
		step: 4,
		section: "screening",
		order: 40,
		label: { en: "We have a sanctions screening program in place" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "sanctionsVendor",
		step: 4,
		section: "screening",
		order: 50,
		label: { en: "Sanctions Screening Vendor" },
		type: "text",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: { en: "e.g. Chainalysis, Elliptic" },
		helpText: { en: "Name the vendor or tool used for sanctions screening." },
		dependsOn: {
			fieldId: "sanctionsScreening",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "sanctionsScreenedItems",
		step: 4,
		section: "screening",
		order: 60,
		label: { en: "Items Screened for Sanctions" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "sanctionsScreening",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "blockchainMonitoring",
		step: 4,
		section: "blockchain",
		order: 70,
		label: { en: "We have a blockchain monitoring program in place" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "monitoringVendor",
		step: 4,
		section: "blockchain",
		order: 80,
		label: { en: "Blockchain Monitoring Vendor" },
		type: "text",
		visible: true,
		required: false,
		validationRules: [],
		placeholder: { en: "e.g. Chainalysis, CipherTrace" },
		helpText: { en: "Name the vendor or tool used for blockchain monitoring." },
		dependsOn: {
			fieldId: "blockchainMonitoring",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "monitoringProcess",
		step: 4,
		section: "blockchain",
		order: 90,
		label: { en: "Monitoring Process" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "blockchainMonitoring",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "monitoringThresholds",
		step: 4,
		section: "blockchain",
		order: 100,
		label: { en: "Monitoring Thresholds" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [],
		dependsOn: {
			fieldId: "blockchainMonitoring",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "regulatoryEnforcement",
		step: 4,
		section: "regulatory",
		order: 110,
		label: { en: "We have been subject to regulatory enforcement actions" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "enforcementDetails",
		step: 4,
		section: "regulatory",
		order: 120,
		label: { en: "Enforcement Action Details" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [
			{
				type: "required",
				message: "Enforcement details are required",
				appliesWhen: {
					fieldId: "regulatoryEnforcement",
					operator: "equals",
					value: true,
				},
			},
		],
		dependsOn: {
			fieldId: "regulatoryEnforcement",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "riskAssessmentUrl",
		step: 4,
		section: "documents",
		order: 130,
		label: { en: "Risk Assessment Document" },
		type: "file",
		visible: true,
		required: false,
		validationRules: [],
		helpText: { en: "Upload your most recent risk assessment." },
	},
	{
		fieldId: "complianceProgramUrl",
		step: 4,
		section: "documents",
		order: 140,
		label: { en: "Compliance Program Document" },
		type: "file",
		visible: true,
		required: false,
		validationRules: [],
		helpText: { en: "Upload your compliance program documentation." },
	},

	// ─── Step 5 — Limits & Controls ─────────────────────────────────────────────
	{
		fieldId: "velocityLimits",
		step: 5,
		section: "limits",
		order: 10,
		label: { en: "Velocity Limits" },
		type: "textarea",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Velocity limits are required" },
		],
		placeholder: { en: "Describe transaction velocity limits" },
	},
	{
		fieldId: "transactionLimits",
		step: 5,
		section: "limits",
		order: 20,
		label: { en: "Transaction Limits" },
		type: "textarea",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Transaction limits are required" },
		],
		placeholder: { en: "Describe per-transaction limits" },
	},
	{
		fieldId: "otherLimits",
		step: 5,
		section: "limits",
		order: 30,
		label: { en: "Other Limits" },
		type: "textarea",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Other limits information is required" },
		],
	},
	{
		fieldId: "blockWalletHolders",
		step: 5,
		section: "blocking",
		order: 40,
		label: { en: "Block Wallet Holders" },
		type: "boolean",
		visible: true,
		required: true,
		validationRules: [],
	},
	{
		fieldId: "blockingCircumstances",
		step: 5,
		section: "blocking",
		order: 50,
		label: { en: "Blocking Circumstances" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [
			{
				type: "required",
				message: "Blocking circumstances are required",
				appliesWhen: {
					fieldId: "blockWalletHolders",
					operator: "equals",
					value: true,
				},
			},
		],
		dependsOn: {
			fieldId: "blockWalletHolders",
			operator: "equals",
			value: true,
		},
	},
	{
		fieldId: "blockingPreventionControls",
		step: 5,
		section: "blocking",
		order: 60,
		label: { en: "Prevention Controls" },
		type: "textarea",
		visible: true,
		required: false,
		validationRules: [
			{
				type: "required",
				message: "Prevention controls are required",
				appliesWhen: {
					fieldId: "blockWalletHolders",
					operator: "equals",
					value: true,
				},
			},
		],
		dependsOn: {
			fieldId: "blockWalletHolders",
			operator: "equals",
			value: true,
		},
	},

	// ─── Step 6 — Contacts ──────────────────────────────────────────────────────
	{
		fieldId: "primaryBusinessContact",
		step: 6,
		section: "contacts",
		order: 10,
		label: { en: "Primary Business Contact" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Primary business contact is required" },
		],
	},
	{
		fieldId: "primaryProductContact",
		step: 6,
		section: "contacts",
		order: 20,
		label: { en: "Primary Product Contact" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Primary product contact is required" },
		],
		helpText: { en: "Stored as 'technical contact' in the system." },
	},
	{
		fieldId: "primaryLegalContact",
		step: 6,
		section: "contacts",
		order: 30,
		label: { en: "Primary Legal Contact" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Primary legal contact is required" },
		],
	},
	{
		fieldId: "primarySigner",
		step: 6,
		section: "contacts",
		order: 40,
		label: { en: "Primary Signer" },
		type: "text",
		visible: true,
		required: true,
		validationRules: [
			{ type: "required", message: "Primary signer is required" },
		],
	},
	{
		fieldId: "ownershipChartUrl",
		step: 6,
		section: "documents",
		order: 50,
		label: { en: "Ownership Structure Chart" },
		type: "file",
		visible: true,
		required: false,
		validationRules: [],
		helpText: {
			en: "Upload a chart showing your complete ownership structure.",
		},
	},
];
